import { Link } from 'react-router-dom'
import SEO, { breadcrumbs } from '../components/common/SEO'
import ResponsiveImage from '../components/common/ResponsiveImage'
import { Breadcrumbs, BookingBand, LinkCards } from '../components/detail/DetailBlocks'
import { blogPosts } from '../data/blog'
import { teamBySlug } from '../data/team'
import { serviceBySlug } from '../data/servicePages'
import { conditionBySlug } from '../data/conditions'
import { SITE_URL } from '../data/site'
import NotFound from './NotFound'

const BlogPostPage = ({ slug }) => {
  const post = blogPosts.find((p) => p.slug === slug)
  if (!post) return <NotFound />

  const author = teamBySlug(post.authorSlug)
  const path = `/blog/${post.slug}`
  const trail = [
    { name: 'Home', path: '/' },
    { name: 'Blog', path: '/blog' },
    { name: post.title, path },
  ]
  const related = [
    ...(post.relatedServices || []).map(serviceBySlug).filter(Boolean).map((s) => ({
      to: `/services/${s.slug}`,
      name: s.name,
      description: s.intro,
    })),
    ...(post.relatedConditions || []).map(conditionBySlug).filter(Boolean).map((c) => ({
      to: `/conditions/${c.slug}`,
      name: c.name,
      description: c.intro,
    })),
  ]
  const formattedDate = new Date(`${post.date}T12:00:00`).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <>
      <SEO
        title={post.title}
        description={post.description || post.excerpt}
        path={path}
        image={post.image}
        imageAlt={post.title}
        type="article"
        publishedTime={post.date}
        modifiedTime={post.updated || post.date}
        author={author?.name}
        noIndex={!post.reviewed}
        structuredData={[
          breadcrumbs(trail),
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.description || post.excerpt,
            image: `${SITE_URL}${post.image}`,
            datePublished: post.date,
            dateModified: post.updated || post.date,
            mainEntityOfPage: `${SITE_URL}${path}`,
            articleSection: post.category,
            author: author
              ? {
                  '@type': 'Person',
                  '@id': `${SITE_URL}/team/${author.slug}#person`,
                  name: author.name,
                  jobTitle: author.fullTitle,
                  url: `${SITE_URL}/team/${author.slug}`,
                }
              : undefined,
            publisher: { '@id': `${SITE_URL}/#organization` },
          },
        ]}
      />

      <article className="bg-cream-200 pt-24 md:pt-28">
        <Breadcrumbs items={trail} />
        <header className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10">
          <span className="inline-block px-3 py-1 bg-cream-100 border border-dark-200/20 text-dark-500 text-[10px] uppercase tracking-[0.15em] font-sans font-medium rounded-full mb-5">
            {post.category}
          </span>
          <h1 className="font-display text-dark-800 text-4xl md:text-5xl font-medium leading-[1.1] mb-6">
            {post.title}
          </h1>
          <p className="font-sans text-dark-400 text-sm">
            {author && (
              <>
                By{' '}
                <Link to={`/team/${author.slug}`} className="text-dark-600 underline underline-offset-4">
                  {author.name}, {author.title}
                </Link>{' '}
                ·{' '}
              </>
            )}
            <time dateTime={post.date}>{formattedDate}</time>
            {post.readTime && <> · {post.readTime}</>}
          </p>
        </header>

        {post.image && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
            <div className="rounded-[20px] overflow-hidden shadow-lg">
              <ResponsiveImage
                src={post.image}
                alt={post.title}
                sizes="(min-width: 1024px) 900px, 100vw"
                loading="eager"
                className="w-full aspect-[16/9] object-cover"
              />
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-10">
          {post.body.map((section, i) => (
            <section key={section.heading || i}>
              {section.heading && (
                <h2 className="font-display text-dark-700 text-2xl md:text-3xl font-semibold mb-4">
                  {section.heading}
                </h2>
              )}
              {section.paragraphs.map((p, j) => (
                <p key={j} className="font-sans text-dark-500 text-base md:text-lg leading-relaxed mb-4">
                  {p}
                </p>
              ))}
            </section>
          ))}
        </div>
      </article>

      <LinkCards label="Keep exploring" heading="Related care at Recreo" items={related} />
      <BookingBand />
    </>
  )
}

export default BlogPostPage
